import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { 
  Search, 
  Loader2, 
  AlertTriangle,
  CheckCircle,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Filter,
  FileQuestion,
  Save,
  X,
  RefreshCw,
  Sparkles,
  List
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * QuizReviewTool - Admin tool to review and edit quiz questions
 */
const QuizReviewTool = ({ api }) => {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [stats, setStats] = useState({ total_questions: 0, questions_with_issues: 0 });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('all');
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);
  const [skills, setSkills] = useState([]);
  
  // Bulk operations
  const [bulkDialog, setBulkDialog] = useState({ open: false, type: null, skillId: null });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [skillSummary, setSkillSummary] = useState(null);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState({ open: false, question: null });
  const [editForm, setEditForm] = useState({
    question: '',
    options: ['', '', '', ''],
    correct_answer: 0,
    explanation: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Fetch skills for filter dropdown
  const fetchSkills = useCallback(async () => {
    try {
      const res = await api.get('/job-simulations/admin/skills-with-quizzes');
      setSkills(res.data.skills || []);
    } catch (err) {
      console.error('Failed to fetch skills:', err);
    }
  }, [api]);

  // Fetch questions with filters
  const fetchQuestions = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pagination.limit);
      
      if (selectedSkill && selectedSkill !== 'all') {
        params.append('skill_id', selectedSkill);
      }
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (showIssuesOnly) {
        params.append('has_issues', 'true');
      }
      
      const res = await api.get(`/job-simulations/admin/quiz-questions?${params}`);
      setQuestions(res.data.questions || []);
      setPagination(res.data.pagination || { page: 1, limit: 10, total: 0, pages: 0 });
      setStats(res.data.stats || { total_questions: 0, questions_with_issues: 0 });
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      toast.error('Failed to load quiz questions');
    } finally {
      setLoading(false);
    }
  }, [api, selectedSkill, searchTerm, showIssuesOnly, pagination.limit]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  useEffect(() => {
    fetchQuestions(1);
  }, [selectedSkill, showIssuesOnly, fetchQuestions]);

  const handleSearch = () => {
    fetchQuestions(1);
  };

  const openEditDialog = (q) => {
    setEditForm({
      question: q.question,
      options: [...q.options],
      correct_answer: q.correct_answer,
      explanation: q.explanation || ''
    });
    setEditDialog({ open: true, question: q });
  };

  const handleSave = async () => {
    if (!editDialog.question) return;
    
    const { skill_id, lesson_id, question_idx } = editDialog.question;
    
    // Validate
    if (!editForm.question.trim()) {
      toast.error('Question text is required');
      return;
    }
    if (editForm.options.some(opt => !opt.trim())) {
      toast.error('All options must have text');
      return;
    }
    
    try {
      setSaving(true);
      await api.put(
        `/job-simulations/admin/quiz-questions/${skill_id}/${lesson_id}/${question_idx}`,
        editForm
      );
      
      toast.success('Question updated successfully');
      setEditDialog({ open: false, question: null });
      fetchQuestions(pagination.page);
    } catch (err) {
      console.error('Failed to save question:', err);
      toast.error(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (q) => {
    try {
      setDeleting(q.question_idx);
      await api.delete(
        `/job-simulations/admin/quiz-questions/${q.skill_id}/${q.lesson_id}/${q.question_idx}`
      );
      
      toast.success('Question deleted');
      fetchQuestions(pagination.page);
    } catch (err) {
      console.error('Failed to delete question:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete question');
    } finally {
      setDeleting(null);
    }
  };

  const updateOption = (index, value) => {
    const newOptions = [...editForm.options];
    newOptions[index] = value;
    setEditForm({ ...editForm, options: newOptions });
  };

  // Bulk Operations
  const openBulkDialog = async (type, skillId) => {
    if (!skillId || skillId === 'all') {
      toast.error('Please select a specific skill first');
      return;
    }
    
    setBulkDialog({ open: true, type, skillId });
    setSkillSummary(null);
    
    // Fetch skill summary
    try {
      const res = await api.get(`/job-simulations/admin/quiz-questions/skill-summary/${skillId}`);
      setSkillSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch skill summary:', err);
    }
  };

  const handleBulkRegenerate = async () => {
    if (!bulkDialog.skillId) return;
    
    try {
      setBulkLoading(true);
      const res = await api.post(`/job-simulations/admin/quiz-questions/bulk-regenerate/${bulkDialog.skillId}`);
      
      toast.success(res.data.message, {
        description: `${res.data.total_lessons_regenerated} lessons updated`
      });
      
      setBulkDialog({ open: false, type: null, skillId: null });
      fetchQuestions(1);
      fetchSkills(); // Refresh skill counts
    } catch (err) {
      console.error('Failed to bulk regenerate:', err);
      toast.error(err.response?.data?.detail || 'Failed to regenerate quizzes');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async (issuesOnly = false) => {
    if (!bulkDialog.skillId) return;
    
    try {
      setBulkLoading(true);
      const res = await api.post(`/job-simulations/admin/quiz-questions/bulk-delete/${bulkDialog.skillId}`, {
        issues_only: issuesOnly
      });
      
      toast.success(res.data.message);
      
      setBulkDialog({ open: false, type: null, skillId: null });
      fetchQuestions(1);
      fetchSkills();
    } catch (err) {
      console.error('Failed to bulk delete:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete quizzes');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileQuestion className="h-5 w-5" />
          Quiz Question Review Tool
        </CardTitle>
        <CardDescription>
          Review, edit, and fix quiz questions across all skills. 
          {stats.questions_with_issues > 0 && (
            <Badge variant="destructive" className="ml-2">
              {stats.questions_with_issues} questions with issues
            </Badge>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="search-questions" className="text-sm">Search</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="search-questions"
                placeholder="Search questions, options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="quiz-search-input"
              />
              <Button onClick={handleSearch} disabled={loading} size="icon">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="w-[200px]">
            <Label className="text-sm">Filter by Skill</Label>
            <Select value={selectedSkill} onValueChange={setSelectedSkill}>
              <SelectTrigger className="mt-1" data-testid="skill-filter">
                <SelectValue placeholder="All Skills" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Skills ({stats.total_questions})</SelectItem>
                {skills.map(skill => (
                  <SelectItem key={skill.skill_id} value={skill.skill_id}>
                    {skill.skill_name} ({skill.total_questions})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button
            variant={showIssuesOnly ? "default" : "outline"}
            onClick={() => setShowIssuesOnly(!showIssuesOnly)}
            className="h-10"
            data-testid="issues-filter"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showIssuesOnly ? 'Showing Issues' : 'Show Issues Only'}
          </Button>
        </div>

        {/* Bulk Operations - Only show when a skill is selected */}
        {selectedSkill && selectedSkill !== 'all' && (
          <div className="flex gap-2 p-3 bg-muted/50 rounded-lg border border-dashed">
            <div className="flex-1">
              <p className="text-sm font-medium">Bulk Operations for Selected Skill</p>
              <p className="text-xs text-muted-foreground">
                Regenerate or delete all quiz questions at once
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBulkDialog('regenerate', selectedSkill)}
              data-testid="bulk-regenerate-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Regenerate All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => openBulkDialog('summary', selectedSkill)}
              data-testid="bulk-summary-btn"
            >
              <List className="h-4 w-4 mr-2" />
              View Summary
            </Button>
          </div>
        )}

        {/* Questions List */}
        <ScrollArea className="h-[400px] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileQuestion className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No questions found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div
                  key={`${q.skill_id}-${q.lesson_id}-${q.question_idx}-${idx}`}
                  className={`p-4 rounded-lg border ${
                    q.has_issues 
                      ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30' 
                      : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Metadata */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge variant="outline" className="text-xs">
                          {q.skill_name}
                        </Badge>
                        <Badge variant="secondary" className="text-xs">
                          {q.lesson_title}
                        </Badge>
                        {q.has_issues && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {q.issue_type === 'problematic_option' ? 'Bad Option' : 'Invalid Answer'}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Question */}
                      <p className="font-medium mb-2">{q.question}</p>
                      
                      {/* Options */}
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {q.options.map((opt, optIdx) => (
                          <div
                            key={optIdx}
                            className={`px-2 py-1 rounded ${
                              optIdx === q.correct_answer
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : 'bg-muted'
                            }`}
                          >
                            {optIdx === q.correct_answer && (
                              <CheckCircle className="h-3 w-3 inline mr-1" />
                            )}
                            {opt}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(q)}
                        data-testid={`edit-question-${idx}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive border-destructive/50 hover:bg-destructive/10"
                            disabled={deleting === q.question_idx}
                            data-testid={`delete-question-${idx}`}
                          >
                            {deleting === q.question_idx ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Question?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete this quiz question. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(q)}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.pages} ({pagination.total} questions)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchQuestions(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchQuestions(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages || loading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => !saving && setEditDialog({ ...editDialog, open })}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Quiz Question</DialogTitle>
              <DialogDescription>
                {editDialog.question?.skill_name} &gt; {editDialog.question?.lesson_title}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Question Text */}
              <div className="space-y-2">
                <Label htmlFor="edit-question">Question</Label>
                <Textarea
                  id="edit-question"
                  value={editForm.question}
                  onChange={(e) => setEditForm({ ...editForm, question: e.target.value })}
                  rows={3}
                  data-testid="edit-question-text"
                />
              </div>
              
              {/* Options */}
              <div className="space-y-3">
                <Label>Options (select correct answer)</Label>
                <RadioGroup
                  value={editForm.correct_answer.toString()}
                  onValueChange={(val) => setEditForm({ ...editForm, correct_answer: parseInt(val) })}
                >
                  {editForm.options.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <RadioGroupItem value={idx.toString()} id={`option-${idx}`} />
                      <Input
                        value={opt}
                        onChange={(e) => updateOption(idx, e.target.value)}
                        placeholder={`Option ${idx + 1}`}
                        className={editForm.correct_answer === idx ? 'border-emerald-500' : ''}
                        data-testid={`edit-option-${idx}`}
                      />
                    </div>
                  ))}
                </RadioGroup>
                <p className="text-xs text-muted-foreground">
                  Click the radio button to mark the correct answer
                </p>
              </div>
              
              {/* Explanation */}
              <div className="space-y-2">
                <Label htmlFor="edit-explanation">Explanation (optional)</Label>
                <Textarea
                  id="edit-explanation"
                  value={editForm.explanation}
                  onChange={(e) => setEditForm({ ...editForm, explanation: e.target.value })}
                  rows={2}
                  placeholder="Explain why this is the correct answer..."
                  data-testid="edit-explanation"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, question: null })} disabled={saving}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-question-btn">
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Operations Dialog */}
        <Dialog open={bulkDialog.open} onOpenChange={(open) => !bulkLoading && setBulkDialog({ ...bulkDialog, open })}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {bulkDialog.type === 'regenerate' ? 'Bulk Regenerate Quizzes' : 'Skill Quiz Summary'}
              </DialogTitle>
              <DialogDescription>
                {skillSummary?.skill_name || 'Loading...'}
              </DialogDescription>
            </DialogHeader>
            
            {skillSummary ? (
              <div className="space-y-4 py-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{skillSummary.total_lessons}</p>
                    <p className="text-xs text-muted-foreground">Lessons</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{skillSummary.total_questions}</p>
                    <p className="text-xs text-muted-foreground">Questions</p>
                  </div>
                  <div className={`p-3 rounded-lg ${skillSummary.total_issues > 0 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-emerald-100 dark:bg-emerald-900/30'}`}>
                    <p className="text-2xl font-bold">{skillSummary.total_issues}</p>
                    <p className="text-xs text-muted-foreground">Issues</p>
                  </div>
                </div>
                
                {/* Lessons breakdown */}
                <div>
                  <p className="text-sm font-medium mb-2">Lessons</p>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {skillSummary.lessons?.map((lesson, idx) => (
                        <div 
                          key={lesson.lesson_id || idx}
                          className={`flex items-center justify-between p-2 rounded border ${
                            lesson.issues_count > 0 ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30' : ''
                          }`}
                        >
                          <span className="text-sm truncate flex-1">{lesson.lesson_title}</span>
                          <div className="flex items-center gap-2 text-xs">
                            <Badge variant="secondary">{lesson.question_count} Q</Badge>
                            {lesson.issues_count > 0 && (
                              <Badge variant="destructive">{lesson.issues_count} issues</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                {bulkDialog.type === 'regenerate' && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800 dark:text-amber-300">Warning</p>
                        <p className="text-amber-700 dark:text-amber-400">
                          This will regenerate ALL {skillSummary.total_questions} quiz questions for this skill using AI. 
                          Existing questions will be replaced. This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setBulkDialog({ open: false, type: null, skillId: null })}
                disabled={bulkLoading}
              >
                Cancel
              </Button>
              {bulkDialog.type === 'regenerate' && (
                <Button
                  onClick={handleBulkRegenerate}
                  disabled={bulkLoading || !skillSummary}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600"
                  data-testid="confirm-bulk-regenerate"
                >
                  {bulkLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Regenerate All Questions
                </Button>
              )}
              {bulkDialog.type === 'summary' && skillSummary?.total_issues > 0 && (
                <Button
                  variant="destructive"
                  onClick={() => handleBulkDelete(true)}
                  disabled={bulkLoading}
                  data-testid="delete-issues-only"
                >
                  {bulkLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Delete Issues Only ({skillSummary.total_issues})
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default QuizReviewTool;
